import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { ActionButtons, ConfirmModal, useToast } from "../../../shared/components/SharedUI";
import {
  getResidentes, crearResidente, updateResidente, deleteResidente,
} from "../services/portalService";

const PAGE_SIZE = 8;

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btnStyle = (disabled) => ({
    padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
    background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
    cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
  });
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 16 }}>
      <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)}>
        <i className="fa-solid fa-chevron-left" />
      </button>
      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{page} / {pages}</span>
      <button style={btnStyle(page === pages)} disabled={page === pages} onClick={() => onChange(page + 1)}>
        <i className="fa-solid fa-chevron-right" />
      </button>
    </div>
  );
}

export default function AdminResidentes() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();

  const [residentes, setResidentes] = useState([]);
  const [filiales,   setFiliales]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null);
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState(null);
  const [confirm, setConfirm]       = useState(null);
  const [deleting, setDeleting]     = useState(false);

  // Filters
  const [search,       setSearch]       = useState("");
  const [filterFilial, setFilterFilial] = useState("");
  const [page, setPage] = useState(1);

  function load() {
    getResidentes().then(setResidentes).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/")
      .then(r => r.json())
      .then(d => setFiliales(d.results || d))
      .catch(() => {});
  }, []);

  function resetPage() { setPage(1); }

  const filtered = residentes.filter(r => {
    if (search) {
      const q = search.toLowerCase();
      const fullName = `${r.first_name} ${r.last_name}`.toLowerCase();
      if (!fullName.includes(q) && !r.username.toLowerCase().includes(q)) return false;
    }
    if (filterFilial && String(r.filial_id) !== filterFilial) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function openCreate() {
    setForm({ username: "", email: "", first_name: "", last_name: "", password: "", filial_id: "" });
    setErr(null); setModal({ mode: "create" });
  }
  function openEdit(r) {
    setForm({ email: r.email, first_name: r.first_name, last_name: r.last_name, filial_id: r.filial_id, password: "" });
    setErr(null); setModal({ mode: "edit", id: r.id });
  }

  async function handleSave() {
    setSaving(true); setErr(null);
    try {
      if (modal.mode === "create") {
        await crearResidente({ ...form, filial_id: parseInt(form.filial_id) });
      } else {
        const payload = { ...form, filial_id: parseInt(form.filial_id) };
        if (!payload.password) delete payload.password;
        await updateResidente(modal.id, payload);
      }
      showToast(t("common.guardado"), true);
      setModal(null); load();
    } catch (e) {
      try { const d = JSON.parse(e.message); setErr(Object.values(d).flat().join(". ")); }
      catch { setErr(e.message); }
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try { await deleteResidente(confirm.id); showToast(t("common.eliminado"), true); setConfirm(null); load(); }
    catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  const field = (key, label, type = "text", required = false, placeholder = "") => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
        {label}{required && <span style={{ color: "#f87171" }}> *</span>}
      </label>
      <input type={type} value={form[key] ?? ""} placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="form-control" />
    </div>
  );

  const inputStyle = {
    padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border-input)",
    background: "var(--hover-bg)", color: "var(--text-primary)", fontSize: 13,
    outline: "none", minWidth: 0,
  };

  return (
    <div style={{ padding: "24px" }}>
      {toastEl}
      <ConfirmModal
        open={!!confirm} name={confirm?.name}
        onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting}
      />

      {/* Breadcrumb */}
      <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>Portal Admin /</span>
        </Link>
        <span style={{ color: "var(--text-primary)", fontWeight: 600 }}> {t("portal.residentes")}</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2" style={{ marginBottom: 16 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("portal.residentes")}</h2>
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>{t("portal.residentesDesc")}</p>
        </div>
        <button onClick={openCreate} style={{
          background: "#f97316", color: "#fff", border: "none", borderRadius: 8,
          padding: "9px 16px", cursor: "pointer", fontWeight: 700, fontSize: 13,
          display: "flex", alignItems: "center", gap: 6, marginTop: 8,
        }}>
          <i className="fa-solid fa-plus" /> {t("portal.nuevo")}
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); resetPage(); }}
          placeholder={t("portal.buscarNombre") || "Buscar nombre…"}
          style={{ ...inputStyle, flex: "1 1 200px" }} />
        <select value={filterFilial} onChange={e => { setFilterFilial(e.target.value); resetPage(); }}
          style={{ ...inputStyle, flex: "1 1 140px" }}>
          <option value="">{t("portal.todasLasFiliales")}</option>
          {filiales.map(f => <option key={f.id} value={f.id}>{f.idFilial}</option>)}
        </select>
        {(search || filterFilial) && (
          <button onClick={() => { setSearch(""); setFilterFilial(""); setPage(1); }} style={{
            padding: "7px 12px", borderRadius: 8, border: "none",
            background: "rgba(249,115,22,0.1)", color: "#f97316",
            cursor: "pointer", fontSize: 12, fontWeight: 700,
          }}>
            <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{t("common.limpiar")}
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>{t("common.cargando")}</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: 48 }}>
          <i className="fa-solid fa-users" style={{ fontSize: 40, opacity: 0.4, marginBottom: 12, display: "block" }} />
          <p style={{ margin: 0 }}>{residentes.length === 0 ? t("portal.sinResidentes") : t("common.sinResultados") || "Sin resultados"}</p>
        </div>
      ) : (
        <>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, overflow: "hidden" }}>
            {paged.map((r, i) => (
              <div key={r.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 16px",
                borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>
                    {r.first_name} {r.last_name}{" "}
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>@{r.username}</span>
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                    {t("portal.filial")}: {r.filial_nombre || "—"}
                    {r.email && ` · ${r.email}`}
                  </p>
                  {!r.is_active && (
                    <span style={{
                      display: "inline-block", marginTop: 4,
                      background: "rgba(156,163,175,0.1)", border: "1px solid rgba(156,163,175,0.3)",
                      color: "#9ca3af", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700,
                    }}>
                      {t("portal.inactivo")}
                    </span>
                  )}
                </div>
                <ActionButtons
                  onEdit={() => openEdit(r)}
                  onDelete={() => setConfirm({ id: r.id, name: `${r.first_name} ${r.last_name}` })}
                />
              </div>
            ))}
          </div>
          <Pagination page={page} total={filtered.length} onChange={setPage} />
        </>
      )}

      {/* Modal */}
      {modal && (
        <div onClick={() => setModal(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(3px)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 12, padding: 24, width: "min(560px,94vw)",
            boxShadow: "0 24px 64px var(--shadow)", maxHeight: "90vh", overflowY: "auto",
          }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 20px" }}>
              {modal.mode === "create" ? t("portal.crearResidente") : t("portal.editarResidente")}
            </h2>
            {modal.mode === "create" && field("username", t("portal.usuario"), "text", true)}
            {modal.mode === "create" && field("password", t("portal.contrasena"), "password", true)}
            {field("first_name", t("portal.nombre"))}
            {field("last_name", t("portal.apellido"))}
            {field("email", t("portal.correo"), "email")}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                {t("portal.filial")} <span style={{ color: "#f87171" }}>*</span>
              </label>
              <select value={form.filial_id ?? ""} onChange={e => setForm(f => ({ ...f, filial_id: e.target.value }))}
                className="form-control">
                <option value="">{t("portal.seleccionarFilial")}</option>
                {filiales.map(f => <option key={f.id} value={f.id}>{f.idFilial}</option>)}
              </select>
            </div>
            {modal.mode === "edit" && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                  {t("portal.nuevaContrasena")}
                </label>
                <input type="password" value={form.password ?? ""} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={t("portal.dejarVacioMantener")} className="form-control" />
              </div>
            )}
            {err && (
              <div style={{
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "4px solid #f87171", borderRadius: 8, padding: "10px 14px",
                color: "#f87171", fontSize: 13, marginBottom: 14,
              }}>
                {err}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setModal(null)} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-input)",
                background: "var(--hover-bg)", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
              }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: "9px 20px", borderRadius: 8, border: "none",
                background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
              }}>
                {saving ? t("common.cargando") : t("common.guardar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
